"use client";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Blog } from "@/types/blog";
import urlIcon from "@/public/images/url.svg";
import trueCircleIcon from "@/public/images/trueCircle.svg";
import ShareButtons from "@/components/shared/ShareButtons";

interface Heading {
  id: string;
  text: string;
}

const BlogContent: React.FC<{ content: string }> = React.memo(({ content }) => (
  <div
    className="blog-content prose prose-lg"
    itemProp="articleBody"
    dangerouslySetInnerHTML={{ __html: content }}
  />
));

const TableOfContents: React.FC<{
  headings: Heading[];
  onHeadingClick: (id: string) => void;
  lang: "ar" | "en";
}> = React.memo(({ headings, onHeadingClick, lang }) => (
  <>
    <p className="text-25 text-darkText font-[500]">
      {lang === "ar" ? "اهم النقاط" : "Important Points"}
    </p>
    <ul className="flex flex-col gap-4">
      {headings.map((heading) => (
        <li key={heading.id} className="flex items-center gap-3">
          <Image src={trueCircleIcon} alt="trueCircle" />
          <button
            className="text-18 text-darkText font-[500]"
            onClick={() => onHeadingClick(heading.id)}
          >
            {heading.text}
          </button>
        </li>
      ))}
    </ul>
    <span className="bg-[#EBEBEB] h-[1px] w-[80%] block mx-auto"></span>
  </>
));
interface Name {
  ar: string;
  en: string;
}
const ImportantLinks: React.FC<{
  links: Blog["links"];
  lang: "ar" | "en";
  name: Name;
}> = React.memo(({ links, lang, name }) => (
  <div className="md:sticky top-[200px] left-0 overflow-hidden">
    <p className="text-25 text-darkText font-[500]">
      {lang === "ar" ? "روابط مهمة" : "Important Links"}
    </p>
    <ul className="flex flex-col gap-4 mt-2">
      {links.map((link, index) => (
        <li key={index} className="flex items-center gap-3">
          <Link
            href={link.link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3"
          >
            <Image src={urlIcon} alt="url" />
            <p className="text-18 text-primaryColor underline font-[500]">
              {link.title[lang]}
            </p>
          </Link>
        </li>
      ))}
    </ul>
  </div>
));

const BlogIndex: React.FC<{ blog: Blog; lang: "ar" | "en" }> = ({
  blog,
  lang,
}) => {
  const sourceContent = blog.text[lang] ?? "";
  // Starting with the actual HTML makes the full article visible in the
  // server response. Googlebot no longer has to execute JavaScript to read it.
  const [content, setContent] = useState(sourceContent);
  const [headings, setHeadings] = useState<Heading[]>([]);
  const router = useRouter();
  const searchParams = useSearchParams();

  const parseContent = useCallback(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(sourceContent, "text/html");
    const headingElements = doc.querySelectorAll("h1, h2, h3, h4, h5, h6");

    const parsedHeadings: Heading[] = Array.from(headingElements)
      .map((heading, index) => {
        const headingText = heading.textContent?.trim() || "";
        if (!headingText) return null; // Skip empty headings
        const headingId = `section-${index}`;
        heading.id = headingId;
        return { id: headingId, text: headingText };
      })
      .filter((heading): heading is Heading => heading !== null); // Filter out null values

    setContent(doc.body.innerHTML);
    setHeadings(parsedHeadings);
  }, [sourceContent]);

  useEffect(() => {
    parseContent();
  }, [parseContent]);

  useEffect(() => {
    const section = searchParams.get("section");
    if (section) {
      scrollToSection(section);
    }
  }, [headings, searchParams]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleHeadingClick = useCallback(
    (id: string) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("section", id);
      scrollToSection(id);
      router.push(`?${newSearchParams.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  const memoizedTableOfContents = useMemo(
    () =>
      headings.length > 0 ? (
        <TableOfContents
          headings={headings}
          onHeadingClick={handleHeadingClick}
          lang={lang}
        />
      ) : null,
    [headings, handleHeadingClick, lang]
  );

  const memoizedImportantLinks = useMemo(
    () =>
      blog.links.length > 0 ? (
        <ImportantLinks links={blog.links} name={blog.title} lang={lang} />
      ) : null,
    [blog.links, lang]
  );

  return (
    <section className="container bg-white rounded-[10px] p-5 flex flex-col-reverse md:flex-row gap-10">
      <div className="grow flex flex-col gap-10">
        <h1 className="text-25 text-darkText font-[600]">{blog.title[lang]}</h1>
        <BlogContent content={content} />
      </div>
      <div className="flex flex-col gap-5 min-w-[300px] xl:min-w-[390px] relative">
        {memoizedTableOfContents}
        {memoizedImportantLinks}
        <div className="flex items-center justify-between w-full gap-3 mt-3">
          <p className="bg-primaryColor py-3  rounded-[10px] text-20 text-white flex items-center justify-center flex-1">
            {lang === "ar" ? "رابط مشاركه المقال" : "Article Sharing Link"}
          </p>

          <ShareButtons blog={true} title={blog?.title[lang]} />
        </div>
      </div>
    </section>
  );
};

export default BlogIndex;
